import { Link, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Plus, Minus, ArrowUpRight } from "lucide-react";
import { variantLabel, variantKey, formatPrice } from "../data/products";
import { toast } from "sonner";
import { useRevealAll } from "../hooks/useReveal";
import { useCart } from "../context/CartContext";
import useStripeProducts, { findProductByStripeId } from "../hooks/useStripeProducts";
import { useSeo, breadcrumbLd, SITE_NAME } from "../hooks/useSeo";
import { postForOrigin } from "../data/blogPosts";

const priceFromDynamic = (product) => {
  const prices = (product.variants || [])
    .map((v) => v.unit_amount)
    .filter((n) => typeof n === "number");
  if (!prices.length) return "- Ft";
  return new Intl.NumberFormat("hu-HU").format(Math.min(...prices)) + " Ft";
};

export default function ProductDetail() {
  const { productId } = useParams();
  const { products: stripeProducts, loading } = useStripeProducts();
  const product = findProductByStripeId(stripeProducts, productId);
  const [variant, setVariant] = useState(null);

  // Initialize/refresh selected variant when Stripe data arrives
  useEffect(() => {
    if (!product) return;
    if (!variant || !product.variants.find((v) => v.length === variant.length && v.weight === variant.weight)) {
      setVariant(product.variants[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const [qty, setQty] = useState(1);
  const ref = useRef(null);
  useRevealAll(ref, [product?.stripe_product_id, loading]);
  const { addItem, openDrawer } = useCart();
  const prices = (product?.variants || []).map((v) => v.unit_amount).filter((n) => typeof n === "number");
  const productPath = product ? `/termek/${product.stripe_product_id}/${product.slug}` : undefined;
  const originLabel = product?.origin === "kazah" ? "kazahsztáni" : "európai";
  useSeo({
    title: product ? `${product.name} – prémium ${originLabel} póthaj | ${SITE_NAME}` : `Póthaj | ${SITE_NAME}`,
    noindex: !product,
    description: product
      ? `${product.name}: prémium ${originLabel} emberi póthaj hajhosszabbításhoz${prices.length ? `, ${new Intl.NumberFormat("hu-HU").format(Math.min(...prices))} Ft-tól` : ""}. Több hosszban és tömegben.`
      : "",
    path: productPath,
    image: product?.image?.endsWith(".svg") ? undefined : product?.image,
    type: "product",
    jsonLd: product && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Product",
          name: product.name,
          description: product.description,
          image: [product.image.startsWith("http") ? product.image : window.location.origin + (product.image.endsWith(".svg") ? "/logo-full.png" : product.image)],
          brand: { "@type": "Brand", name: SITE_NAME },
          category: "Póthaj / hajhosszabbítás",
          ...(prices.length && {
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "HUF",
              lowPrice: Math.min(...prices),
              highPrice: Math.max(...prices),
              offerCount: prices.length,
              availability: "https://schema.org/InStock",
              url: window.location.origin + productPath,
            },
          }),
        },
        breadcrumbLd([["Kezdőlap", "/"], ["Kollekció", "/termekek"], [product.name, productPath]]),
      ],
    },
  });

  if (loading) {
    return (
      <main className="pt-40 he-container min-h-[60vh]">
        <p className="overline mb-5">Betöltés</p>
        <h1 className="font-serif-display text-2xl">Termékek betöltése...</h1>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-40 he-container min-h-[60vh]">
        <p className="overline mb-5">Hiba</p>
        <h1 className="font-serif-display text-4xl mb-6">A termék nem található.</h1>
        <Link to="/termekek" className="btn-outline-gold" data-testid="back-to-products">
          <span>Vissza a kollekcióhoz</span>
        </Link>
      </main>
    );
  }

  const related = (stripeProducts || [])
    .filter((p) => p.stripe_product_id !== product.stripe_product_id && p.origin === product.origin)
    .slice(0, 3);

  const hasNamedVariants = product.variants.length > 0 &&
    (product.variants[0].length || product.variants[0].weight);

  const addToCart = () => {
    if (!variant) return;
    addItem(product, variant, qty);
    toast.success("Kosárba téve", {
      description: `${product.name} · ${variantLabel(variant) || "1 db"} · ${qty} db`,
    });
    openDrawer();
  };

  return (
    <main ref={ref} data-testid="page-product-detail" className="pt-28 md:pt-32 bg-[color:var(--he-bg)]">
      <div className="he-container py-8 flex items-center justify-between">
        <Link
          to="/termekek"
          className="btn-ghost"
          data-testid="back-to-collection"
        >
          <ChevronLeft size={14} className="ghost-arrow" />
          <span>Vissza a kollekcióhoz</span>
        </Link>
        <span className="overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline">
          {product.origin === "europai" ? "Európai forrás" : "Kazahsztáni forrás"}
        </span>
      </div>

      <section className="he-container grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 pb-32">
        <div className="md:col-span-7">
          <div className="he-img-wrap aspect-[4/5] reveal">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover he-img"
              data-testid="product-image"
            />
            {/* Floating badge */}
            <div className="absolute top-6 left-6 flex items-center gap-3">
              <span className="figure italic text-[color:var(--he-gold)] text-3xl">
                {product.origin === "europai" ? "EU" : "KZ"}
              </span>
            </div>
          </div>
        </div>

        <div className="md:col-span-5 md:pt-10">
          <p className="overline mb-3 mag-marker reveal">
            <span>{product.origin === "europai" ? "Európai forrás" : "Kazahsztáni forrás"}</span>
          </p>
          <h1
            className="font-serif-display text-[2.4rem] md:text-[3.4rem] tracking-[-0.02em] leading-[1.04] reveal delay-1"
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <div className="mt-6 flex items-baseline gap-4 reveal delay-2">
            <span className="overline-xs text-[color:var(--he-ink-soft)]">
              {product.variants.length > 1 ? "választott ár" : "ár"}
            </span>
            <span className="font-serif-display text-3xl text-[color:var(--he-ink)]" data-testid="product-price">
              {typeof variant?.unit_amount === "number"
                ? formatPrice(variant.unit_amount)
                : priceFromDynamic(product)}
            </span>
          </div>

          <div className="my-10 he-divider" />

          <p className="text-[color:var(--he-ink-soft)] font-light leading-[1.85] reveal delay-2">
            {product.description}
          </p>

          {/* Variant options — only when there are named variants */}
          {hasNamedVariants && (
            <div className="mt-12 reveal delay-3">
              <div className="flex items-center justify-between mb-5">
                <p className="overline-xs text-[color:var(--he-ink-soft)]">
                  {product.variants.length > 1 ? "Válassz méretet" : "Méret"}
                </p>
                {variant && (
                  <p className="overline-xs text-[color:var(--he-ink-soft)]">
                    Kiválasztva · {variantLabel(variant)}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((v) => {
                  const key = variantKey(v);
                  const isActive = variant && variantKey(variant) === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setVariant(v)}
                      data-testid={`variant-${key}`}
                      className={`he-pill ${isActive ? "active" : ""}`}
                    >
                      {variantLabel(v)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity + CTA */}
          <div className="mt-12 flex items-center gap-5">
            <div className="flex items-center border border-[color:var(--he-line)] rounded-full">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                aria-label="Csökkentés"
                data-testid="qty-minus"
                className="px-4 py-3 hover:text-[color:var(--he-gold)]"
              >
                <Minus size={14} />
              </button>
              <span className="w-10 text-center font-serif-display text-xl" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty(qty + 1)}
                aria-label="Növelés"
                data-testid="qty-plus"
                className="px-4 py-3 hover:text-[color:var(--he-gold)]"
              >
                <Plus size={14} />
              </button>
            </div>
            <button onClick={addToCart} data-testid="add-to-cart" className="btn-ink flex-1">
              <span>Kosárba</span>
            </button>
          </div>

          {/* Spec table */}
          <div className="mt-16">
            <p className="overline-xs text-[color:var(--he-ink-soft)] mb-6">Adatlap</p>
            <div className="border-t border-[color:var(--he-line)]">
              <Spec label="Származás" value={product.origin === "europai" ? "Európai" : "Kazahsztáni"} />
              {hasNamedVariants && (
                <Spec label="Elérhető méretek" value={product.variants.map(variantLabel).join(" · ")} />
              )}
            </div>
          </div>

          {postForOrigin(product.origin) && (
            <Link
              to={`/blog/${postForOrigin(product.origin).slug}`}
              className="mt-10 block border border-[color:var(--he-line)] p-6 hover:border-[color:var(--he-gold)] transition-colors duration-300"
              data-testid="product-blog-link"
            >
              <p className="overline-xs text-[color:var(--he-gold)] mb-2">Útmutató a blogon</p>
              <p className="font-serif-display text-xl leading-snug">{postForOrigin(product.origin).title}</p>
            </Link>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[color:var(--he-cream)] py-28 md:py-36">
          <div className="he-container">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 items-end">
              <div className="md:col-span-7">
                <p className="overline mb-5 mag-marker">
                  <span>Hasonló termékek</span>
                </p>
                <h2 className="font-serif-display text-[2.4rem] md:text-[4rem] leading-[1.05] tracking-[-0.018em]">
                  Ezek <span className="italic text-[color:var(--he-gold)]">is érdekelhetnek.</span>
                </h2>
              </div>
              <div className="md:col-span-3 md:col-start-10">
                <Link to="/termekek" className="btn-ghost" data-testid="related-view-all">
                  <span>Összes termék</span>
                  <ArrowUpRight size={14} className="ghost-arrow" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {related.map((p, i) => (
                <Link
                  key={p.stripe_product_id}
                  to={`/termek/${p.stripe_product_id}/${p.slug}`}
                  className="group reveal"
                  style={{ transitionDelay: `${i * 120}ms` }}
                  data-testid={`related-${p.stripe_product_id}`}
                >
                  <div className="he-img-wrap aspect-[3/4]">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="he-img w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute top-4 left-4 figure italic text-[color:var(--he-gold)] text-2xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="overline-xs text-[color:var(--he-ink-soft)] mt-5 mb-2">
                    {p.origin === "europai" ? "Európai" : "Kazahsztáni"}
                  </p>
                  <h3 className="font-serif-display text-xl md:text-2xl">{p.name}</h3>
                  <p className="font-serif-display text-base text-[color:var(--he-ink-soft)] mt-2">
                    {priceFromDynamic(p)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function Spec({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-6 py-5 border-b border-[color:var(--he-line)] hover:bg-[color:var(--he-cream)]/60 transition-colors duration-300 px-1">
      <span className="overline-xs text-[color:var(--he-ink-soft)] shrink-0">{label}</span>
      <span className="text-right font-light text-[color:var(--he-ink)]">{value}</span>
    </div>
  );
}
